import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listLossRunsByOrg, analyzeLossRun } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { TrendingDown, TrendingUp, AlertTriangle, Sparkles, ArrowRight, Loader2, RefreshCw } from 'lucide-react';

/**
 * LossRunInsights — compact widget for Organization Detail.
 * Pulls the latest analyzed loss run for the org and surfaces:
 *   • Executive summary
 *   • Top 3 recommendations
 *   • Top causes (chips)
 * Provides a one-click "View full analysis" link to /loss-runs.
 */
export default function LossRunInsights({ orgId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await listLossRunsByOrg(orgId);
      setItems(res.data || []);
    } catch (e) {
      // 403 for non-assigned brokers — silently hide
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orgId]);

  const latestAnalyzed = items.find(it => it.analysis_status === 'completed' && it.analysis);
  const latestAny = items[0];
  const showcase = latestAnalyzed || latestAny;

  const reAnalyze = async () => {
    if (!latestAny?.id) return;
    setAnalyzing(true);
    try {
      await analyzeLossRun(latestAny.id);
      toast.success('Analysis refreshed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) return null;
  if (items.length === 0) return null; // hide widget if no loss runs

  const a = showcase?.analysis;

  return (
    <Card className="bg-card border-border" data-testid="loss-run-insights-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="font-semibold flex items-center gap-2 text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
              <TrendingDown className="w-5 h-5 text-primary" />
              Loss Run Insights
              <Badge variant="outline" className="ml-2 text-xs font-normal">{items.length} on file</Badge>
            </h3>
            {showcase && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {showcase.original_filename} · uploaded {new Date(showcase.created_at).toLocaleDateString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {latestAny && (
              <Button variant="outline" size="sm" onClick={reAnalyze} disabled={analyzing} data-testid="org-detail-reanalyze-loss-run">
                {analyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                {latestAnalyzed ? 'Re-analyze' : 'Analyze'}
              </Button>
            )}
            <Button size="sm" className="btn-primary-glow" onClick={() => navigate('/loss-runs')} data-testid="org-detail-view-loss-runs">
              View full analysis <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {!latestAnalyzed ? (
          <p className="text-sm text-muted-foreground">
            Loss runs are uploaded but not yet analyzed. Click "Analyze" to generate AI insights.
          </p>
        ) : (
          <div className="space-y-4">
            {a.executive_summary && (
              <div className="p-3 rounded-md border border-purple-500/30 bg-purple-500/5">
                <div className="flex items-center gap-2 mb-1 text-purple-700 dark:text-purple-300 font-semibold text-sm">
                  <Sparkles className="w-4 h-4" /> Executive Summary
                </div>
                <p className="text-sm">{a.executive_summary}</p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              {!!(a.top_causes || []).length && (
                <div className="p-3 rounded-md border border-border bg-secondary/30">
                  <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Top causes
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {a.top_causes.slice(0, 6).map((c, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {c.cause}{typeof c.share_pct === 'number' ? ` · ${c.share_pct}%` : ''}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {(a.total_claims_estimate != null || a.total_incurred_estimate) && (
                <div className="p-3 rounded-md border border-border bg-secondary/30">
                  <div className="text-xs text-muted-foreground mb-2">At a glance</div>
                  <div className="text-sm space-y-0.5">
                    {a.total_claims_estimate != null && <div>Claims: <span className="font-semibold">{a.total_claims_estimate}</span></div>}
                    {a.total_incurred_estimate && <div>Total Incurred: <span className="font-semibold">{a.total_incurred_estimate}</span></div>}
                  </div>
                </div>
              )}
            </div>

            {!!(a.recommendations || []).length && (
              <div className="p-3 rounded-md border border-primary/30 bg-primary/5">
                <div className="flex items-center gap-2 mb-2 font-semibold text-sm">
                  <Sparkles className="w-4 h-4 text-primary" /> Top recommendations for the client
                </div>
                <ul className="text-sm list-disc pl-5 space-y-1">
                  {a.recommendations.slice(0, 3).map((r, i) => (<li key={i}>{r}</li>))}
                </ul>
              </div>
            )}

            {!!(a.high_risk_patterns || []).length && (
              <div className="p-3 rounded-md border border-amber-500/30 bg-amber-500/5">
                <div className="flex items-center gap-2 mb-2 font-semibold text-sm">
                  <TrendingUp className="w-4 h-4 text-amber-500" /> High-risk patterns
                </div>
                <ul className="text-sm list-disc pl-5 space-y-1">
                  {a.high_risk_patterns.slice(0, 3).map((p, i) => (<li key={i}>{p}</li>))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
